/**
 * Trip CO₂ Calculator — mode/sub-mode types, defaults, and helpers.
 *
 * Single source of truth for the seven supported transport modes:
 * what's user-selectable, what's locked, and what each mode's default
 * config is. The shape of `ModeConfig<M>` mirrors the backend API
 * options shape (per `backend/services/carbonAccounting/factors.js`)
 * so `toApiOptions` is essentially a typed pass-through.
 *
 * Phase 1 of the Trip CO₂ Calculator (see
 * `docs/features/2026.05.09 - Trip CO2 Calculator v1.md` §5.2 + §11).
 *
 * Naming notes:
 * - The frontend uses the backend's `airplane` mode key (not `plane`)
 *   to avoid a translation layer. UI display strings live in
 *   `MODE_DISPLAY` (`'✈️'` + `'Plane'`).
 * - Locked fields (e.g. train type, plane cabin) are still part of
 *   `ModeConfig<M>` so that `toApiOptions` can emit them explicitly
 *   on every request — the calculator's behaviour must not depend on
 *   backend default options, which differ from this spec for bus/car/van.
 */

import type { TransportMode } from '../admin/carbonAccounting/api';

export type { TransportMode };

// ─────────────────────────────────────────────────────────────────────────────
// Sub-option enums (mirrors backend factor table)
// ─────────────────────────────────────────────────────────────────────────────

export const TRAIN_TYPES = ['electric', 'diesel'] as const;
export type TrainType = (typeof TRAIN_TYPES)[number];

export const BUS_TYPES = ['urban', 'coach'] as const;
export type BusType = (typeof BUS_TYPES)[number];

export const PLANE_CABINS = ['economy', 'business'] as const;
export type PlaneCabin = (typeof PLANE_CABINS)[number];

export const CAR_FUELS = ['diesel', 'petrol', 'hybrid', 'electric'] as const;
export type CarFuel = (typeof CAR_FUELS)[number];

/** Van fuel is locked to diesel server-side (no other factors exist). */
export const VAN_FUELS = ['diesel'] as const;
export type VanFuel = (typeof VAN_FUELS)[number];

/** Motorcycle fuel is locked to petrol server-side. */
export const MOTORCYCLE_FUELS = ['petrol'] as const;
export type MotorcycleFuel = (typeof MOTORCYCLE_FUELS)[number];

export const FERRY_PASSENGER_TYPES = ['foot', 'carPassenger', 'average'] as const;
export type FerryPassengerType = (typeof FERRY_PASSENGER_TYPES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Per-mode config shapes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `TransportMode` to its API-shaped config object. The keys of
 * each config are exactly the keys the backend expects in the
 * `options` payload — see `backend/services/carbonAccounting/factors.js`.
 */
export interface ConfigByMode {
  train: { type: TrainType };
  bus: { type: BusType };
  airplane: { cabin: PlaneCabin };
  car: { fuel: CarFuel; occupancy: number };
  van: { fuel: VanFuel; occupancy: number };
  motorcycle: { fuel: MotorcycleFuel; occupancy: number };
  ferry: { passengerType: FerryPassengerType };
}

/** Generic config for a specific mode, for API/use-site type inference. */
export type ModeConfig<M extends TransportMode> = ConfigByMode[M];

/**
 * Discriminated union pairing a mode with its config. Useful for
 * carrying `(mode, config)` together in component state without
 * losing narrowing.
 */
export type AnyModeConfig = {
  [M in TransportMode]: { mode: M; config: ConfigByMode[M] };
}[TransportMode];

// ─────────────────────────────────────────────────────────────────────────────
// Locked defaults (PRD §5.2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default config per mode. These are *the* defaults the calculator
 * uses — independent of the backend's default options, which differ
 * for bus (urban), car (diesel/1pax), and van (1pax).
 *
 * Frozen so accidental mutation surfaces immediately in tests.
 */
export const DEFAULT_CONFIG: { readonly [M in TransportMode]: Readonly<ConfigByMode[M]> } =
  Object.freeze({
    train: Object.freeze({ type: 'electric' as const }),
    bus: Object.freeze({ type: 'coach' as const }),
    airplane: Object.freeze({ cabin: 'economy' as const }),
    car: Object.freeze({ fuel: 'petrol' as const, occupancy: 2 }),
    van: Object.freeze({ fuel: 'diesel' as const, occupancy: 2 }),
    motorcycle: Object.freeze({ fuel: 'petrol' as const, occupancy: 1 }),
    ferry: Object.freeze({ passengerType: 'average' as const }),
  });

// ─────────────────────────────────────────────────────────────────────────────
// What's user-editable per mode (drives InputsPanel sub-field rendering)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * For each mode, the keys of `ModeConfig<M>` that the UI exposes as
 * editable controls. Locked keys (e.g. plane cabin, train type) are
 * absent here, so InputsPanel can render fields by iterating this list.
 *
 * PRD §5.2:
 * - Train / Bus / Plane / Ferry: no sub-fields visible.
 * - Car: passengers (1–7) + fuel.
 * - Van: passengers (1–4); fuel is locked.
 * - Motorcycle: passengers (1–2); fuel is locked.
 */
export const USER_EDITABLE_KEYS = {
  train: [] as ReadonlyArray<keyof ConfigByMode['train']>,
  bus: [] as ReadonlyArray<keyof ConfigByMode['bus']>,
  airplane: [] as ReadonlyArray<keyof ConfigByMode['airplane']>,
  car: ['fuel', 'occupancy'] as ReadonlyArray<keyof ConfigByMode['car']>,
  van: ['occupancy'] as ReadonlyArray<keyof ConfigByMode['van']>,
  motorcycle: ['occupancy'] as ReadonlyArray<keyof ConfigByMode['motorcycle']>,
  ferry: [] as ReadonlyArray<keyof ConfigByMode['ferry']>,
} as const;

/**
 * Occupancy ranges for vehicle modes. These are the practical maxima
 * the dropdown exposes, not the modes' theoretical capacities:
 *   • Car: 5 — a typical 5-seater family car. Larger SUVs / vans of
 *     people exist but at that scale the user should pick "Van".
 *   • Van: 7 — a typical 7-seater minivan / shared-shuttle vehicle.
 *   • Motorcycle: 2 — rider + pillion.
 */
export const OCCUPANCY_RANGES = Object.freeze({
  car: Object.freeze({ min: 1, max: 5 }),
  van: Object.freeze({ min: 1, max: 7 }),
  motorcycle: Object.freeze({ min: 1, max: 2 }),
});

// ─────────────────────────────────────────────────────────────────────────────
// UI display metadata
// ─────────────────────────────────────────────────────────────────────────────

/** Display order for the mode dropdown (PRD §5.1). */
export const MODE_ORDER: ReadonlyArray<TransportMode> = [
  'airplane',
  'train',
  'bus',
  'car',
  'van',
  'motorcycle',
  'ferry',
] as const;

/** Emoji + display label per mode (PRD §5.1, §5.2). */
export const MODE_DISPLAY: Readonly<Record<TransportMode, { emoji: string; label: string }>> =
  Object.freeze({
    airplane: { emoji: '✈️', label: 'Plane' },
    train: { emoji: '🚄', label: 'Train' },
    bus: { emoji: '🚌', label: 'Bus' },
    car: { emoji: '🚗', label: 'Car' },
    van: { emoji: '🚐', label: 'Van' },
    motorcycle: { emoji: '🏍️', label: 'Motorcycle' },
    ferry: { emoji: '⛴️', label: 'Ferry' },
  });

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a fresh copy of the default config for a mode. The copy is
 * mutable — callers can reduce passengers, swap fuel, etc. without
 * worrying about aliasing the frozen default.
 */
export function getDefaultConfig<M extends TransportMode>(mode: M): ConfigByMode[M] {
  return { ...DEFAULT_CONFIG[mode] } as ConfigByMode[M];
}

/**
 * True if the given config differs in any field from the mode's
 * locked default. Used by `buildComparison` (PRD §5.5) to decide
 * whether to *replace* a default chart row with the user's custom
 * row, and to render `(3 pax · electric)` style suffixes.
 */
export function isCustom<M extends TransportMode>(
  mode: M,
  config: ConfigByMode[M]
): boolean {
  const def = DEFAULT_CONFIG[mode] as Record<string, unknown>;
  const cfg = config as unknown as Record<string, unknown>;
  const defKeys = Object.keys(def);
  const cfgKeys = Object.keys(cfg);
  if (defKeys.length !== cfgKeys.length) return true;
  for (const key of defKeys) {
    if (cfg[key] !== def[key]) return true;
  }
  return false;
}

/**
 * Maps a frontend `(mode, config)` to the API `options` payload sent
 * to `POST /api/public/co2/calculate-batch`. Today this is a typed
 * pass-through — `ConfigByMode[M]` was designed to mirror the API
 * shape exactly. Kept as a function so future divergence has a
 * dedicated boundary to live in.
 */
export function toApiOptions<M extends TransportMode>(
  _mode: M,
  config: ConfigByMode[M]
): Record<string, unknown> {
  return { ...config };
}
